import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { isEmpty } from '@witmetrics/utils/misc';
import {
  useActiveAccountLogo,
  useDateFormatPreference,
  useForm,
  useFormSubmission,
  useFormVersion,
  useFormVersionFields,
  useTimeFormatPreference,
} from '@witmetrics/data-store';
import type { FieldAnswer } from '@witmetrics/unison-core';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import { buildPdfFile } from '@/utils/files';
import { createPdfPreview } from '@/utils/pdfs';
import type { Answers } from '@/providers/FormProvider';
import CustomPdf from '@/components/CustomPdf';
import Icon from '@/components/Icon';
import { FormIcon } from '@/icons';
import type { PreviewRun } from './types';
import { classes } from './utils';

export type FormPreviewImageProps = {
  className?: string;
  style?: CSSProperties;
  versionId?: number;
  submissionId?: number;
  answers: Answers;
  fallback?: ReactNode;
  alt?: string;
};

export default function FormPreviewImage({
  className,
  style,
  versionId,
  submissionId,
  answers,
  fallback = <Icon icon={<FormIcon />} />,
  alt,
}: FormPreviewImageProps) {
  const logo = useActiveAccountLogo();
  const dateFormat = useDateFormatPreference();
  const timeFormat = useTimeFormatPreference();
  const formVersion = useFormVersion(versionId);
  const form = useForm(formVersion?.formId);
  const fields = useFormVersionFields(versionId);
  const submission = useFormSubmission(submissionId);
  const { loadedFiles, addLoadedFile } = useLoadedFiles();
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const data = useMemo<FieldAnswer[]>(
    () =>
      fields.map((field) => ({
        field,
        value: answers[field.id] || '',
      })),
    [fields, answers]
  );
  const cacheKey = useMemo(
    () =>
      submissionId
        ? `submission-preview-${submissionId}-${submission?.updatedAt ?? ''}`
        : null,
    [submissionId, submission?.updatedAt]
  );

  useEffect(() => {
    const cached = cacheKey
      ? (loadedFiles[cacheKey] as string | undefined)
      : null;
    if (cached || !form || isEmpty(data)) return;
    const run: PreviewRun = { cancelled: false, url: null };
    generatePreview(run);
    return () => {
      run.cancelled = true;
      // Only revoke URLs the provider doesn't own — cached entries outlive this component.
      if (run.url && !cacheKey) URL.revokeObjectURL(run.url);
    };
  }, [
    cacheKey,
    loadedFiles,
    form,
    data,
    logo,
    dateFormat,
    timeFormat,
    addLoadedFile,
  ]);

  const generatePreview = async (run: PreviewRun) => {
    if (!form) return;
    try {
      const pdfFile = await buildPdfFile(
        form.name,
        <CustomPdf
          logoSrc={logo!}
          form={form}
          dateFormat={dateFormat}
          timeFormat={timeFormat}
          data={data}
        />
      );
      const previewBlob = await createPdfPreview(pdfFile);
      if (run.cancelled) return;
      run.url = URL.createObjectURL(previewBlob);
      if (cacheKey) {
        addLoadedFile(cacheKey, run.url);
      } else {
        setLocalPreview(run.url);
      }
    } catch (err) {
      console.warn('Failed to generate form preview', err);
    }
  };

  const previewSource =
    (cacheKey && (loadedFiles[cacheKey] as string | undefined)) || localPreview;
  // eslint-disable-next-line witmetrics/no-multiline-comments
  /* Wrapped in a fragment so the return type narrows to JSX.Element regardless
    of fallback's ReactNode shape (string, array, etc. aren't valid JSX.Element on their own).
   */
  if (!previewSource) return <>{fallback}</>;
  return (
    <img
      style={style}
      className={twMerge(classes.image, className)}
      src={previewSource}
      alt={alt ?? form?.name ?? 'Form preview'}
    />
  );
}
